import React, { useLayoutEffect, useRef } from "react";

const LINE_HEIGHT = 19;

// Each numbered point is its own row with its own auto-growing textarea.
// Previously every point lived inside ONE shared textarea, numbered by
// splitting its value on "\n" against a separate grey gutter column that
// assumed a fixed height per line — that drifted out of sync whenever a
// point wrapped to more than one visual row, which read as "wrong
// numbering". Making each point its own row means the number is a true DOM
// sibling of that point's own content, so it can never misalign, and a point
// can never silently vanish independently of the array itself (that part of
// the original bug was actually multilineStringToList dropping blank lines
// on save, in careplanNew.js — fixed separately).
const NumberedTextArea = ({
  label,
  placeholder = "Enter text here...",
  value = "",
  onChange,
  readOnly = false,
  minHeight = 60,
}) => {
  const lines = (value || "").split("\n");
  const lineRefs = useRef([]);
  const pendingFocusRef = useRef(null);

  const resizeLine = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // Each line's textarea is only as tall as its own content (via
  // resizeLine above), but the box itself can be much taller (minHeight
  // prop) — e.g. a single short "Medications" entry leaves empty space
  // below/around it that isn't part of any textarea, so clicking there did
  // nothing. Clicking anywhere in the box (that isn't already a textarea,
  // which keeps its own native click-to-position-cursor behavior) now
  // focuses whichever line's row the click landed at or above, cursor at
  // the end of that line's text — same as clicking below the last line of
  // a normal multi-line textarea.
  const focusNearestLine = (clientY) => {
    const nodes = lineRefs.current;
    if (!nodes.length) return;
    let target = nodes[nodes.length - 1];
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (!el) continue;
      if (clientY < el.getBoundingClientRect().bottom) {
        target = el;
        break;
      }
    }
    if (target) {
      target.focus();
      const pos = target.value.length;
      target.setSelectionRange(pos, pos);
    }
  };

  const handleContainerClick = (e) => {
    if (e.target.tagName === "TEXTAREA") return;
    focusNearestLine(e.clientY);
  };

  useLayoutEffect(() => {
    if (readOnly) return;
    lineRefs.current.forEach(resizeLine);
    if (pendingFocusRef.current) {
      const { index, pos } = pendingFocusRef.current;
      pendingFocusRef.current = null;
      const el = lineRefs.current[index];
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    }
  }, [value, readOnly]);

  useLayoutEffect(() => {
    if (readOnly) return;
    const handleResize = () => lineRefs.current.forEach(resizeLine);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [readOnly]);

  const emitLines = (newLines) => onChange && onChange(newLines.join("\n"));

  const updateLine = (index, text) => {
    const newLines = [...lines];
    newLines[index] = text;
    emitLines(newLines);
  };

  // Enter splits the current point into two at the cursor (matches how
  // Enter behaves inside a single shared textarea); Backspace at the very
  // start of a point merges it into the end of the previous one. Both skip
  // while an IME composition is in progress so composing text can't be cut
  // short by an in-flight Enter/Backspace.
  const handleKeyDown = (index, e) => {
    if (e.nativeEvent?.isComposing) return;
    const el = e.target;

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const before = lines[index].slice(0, el.selectionStart);
      const after = lines[index].slice(el.selectionEnd);
      const newLines = [...lines];
      newLines[index] = before;
      newLines.splice(index + 1, 0, after);
      pendingFocusRef.current = { index: index + 1, pos: 0 };
      emitLines(newLines);
    } else if (
      e.key === "Backspace" &&
      el.selectionStart === 0 &&
      el.selectionEnd === 0 &&
      index > 0
    ) {
      e.preventDefault();
      const mergePos = lines[index - 1].length;
      const newLines = [...lines];
      newLines[index - 1] = lines[index - 1] + lines[index];
      newLines.splice(index, 1);
      pendingFocusRef.current = { index: index - 1, pos: mergePos };
      emitLines(newLines);
    }
  };

  // Pasting multi-line text into one point splits it across new points,
  // same as typing each line by hand would.
  const handlePaste = (index, e) => {
    const pasted = e.clipboardData?.getData("text");
    if (!pasted || !pasted.includes("\n")) return;
    e.preventDefault();
    const el = e.target;
    const before = lines[index].slice(0, el.selectionStart);
    const after = lines[index].slice(el.selectionEnd);
    const pastedLines = pasted.split("\n");
    const newLines = [...lines];
    newLines.splice(
      index,
      1,
      before + pastedLines[0],
      ...pastedLines.slice(1, -1),
      pastedLines[pastedLines.length - 1] + after
    );
    pendingFocusRef.current = {
      index: index + pastedLines.length - 1,
      pos: pastedLines[pastedLines.length - 1].length,
    };
    emitLines(newLines);
  };

  if (readOnly) {
    // View-only per Figma: grey numbers, no box — just a bottom border under
    // the whole field, content rendered as plain text (nothing here is ever
    // edited, so there's no auto-grow/measurement logic that could drift).
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
        {label && <label style={{ color: "#565975", fontSize: "14px" }}>{label}</label>}
        <div
          style={{
            width: "100%",
            minHeight: `${minHeight}px`,
            boxSizing: "border-box",
            borderBottom: "1px solid #C9CBD5",
            padding: "9px 12px 9px 0",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {lines.map((line, i) => (
            <div key={i} style={{ display: "flex", gap: "8px" }}>
              <span
                style={{
                  color: "#565975",
                  fontSize: "14px",
                  lineHeight: `${LINE_HEIGHT}px`,
                  flexShrink: 0,
                  minWidth: "14px",
                }}
              >
                {i + 1}.
              </span>
              <span
                style={{
                  color: "#0D1139",
                  fontSize: "14px",
                  lineHeight: `${LINE_HEIGHT}px`,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {line}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
      {label && <label style={{ color: "#0D1139", fontSize: "14px" }}>{label}</label>}

      <div
        onClick={handleContainerClick}
        style={{
          width: "100%",
          minHeight: `${minHeight}px`,
          border: "1px solid #C9CBD5",
          borderRadius: "6px",
          background: "#F8FAFD",
          display: "grid",
          gridTemplateColumns: "17px 1fr",
          gridTemplateRows: lines
            .map((_, i) => (i === lines.length - 1 ? "minmax(min-content, 1fr)" : "min-content"))
            .join(" "),
          overflow: "hidden",
          cursor: "text",
        }}
      >
        {lines.map((line, i) => (
          <React.Fragment key={i} >
            <div
              style={{
                backgroundColor: "#C9CBD5",
                color: "#0D1139",
                textAlign: "right",
                padding: "2px 2px 8px",
                fontSize: "14px",
                lineHeight: `${LINE_HEIGHT}px`,
                userSelect: "none",
                borderTopLeftRadius: i === 0 ? "6px" : 0,
                borderBottomLeftRadius: i === lines.length - 1 ? "6px" : 0,
              }}
            >
              {i + 1}.
            </div>
            <textarea
              ref={(el) => (lineRefs.current[i] = el)}
              value={line}
              placeholder={lines.length === 1 && !line ? placeholder : undefined}
              onChange={(e) => updateLine(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              rows={1}
              style={{
                width: "100%",
                padding: "2px 12px",
                border: "none",
                outline: "none",
                resize: "none",
                background: "transparent",
                color: "#565975",
                fontSize: "14px",
                lineHeight: `${LINE_HEIGHT}px`,
                overflow: "hidden",
                marginBottom:"0px",
                  minHeight:"20px",
              }}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default NumberedTextArea;
